import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";

/** Number of modals currently open (shared so we only unlock body when last closes). */
let openModalCount = 0;

@Component({
  selector: "app-modal",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./modal.component.html",
})
export class ModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() size: "sm" | "md" | "lg" | "xl" = "md";
  @Input() dismissible = true;
  /** When true, modal content wrapper has no padding (e.g. for edit profile with edge nav). */
  @Input() noContentPadding = false;
  @Output() isOpenChange = new EventEmitter<boolean>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isOpen"]) {
      this.updateBodyScrollLock(this.isOpen);
    }
  }

  ngOnDestroy(): void {
    if (this.isOpen) {
      this.updateBodyScrollLock(false);
    }
  }

  /**
   * Lock or unlock body scroll so the page behind the modal doesn't scroll
   * (e.g. when using Lenis smooth scroll). Uses a counter for multiple modals.
   */
  private updateBodyScrollLock(open: boolean): void {
    if (typeof document === "undefined") return;
    if (open) {
      openModalCount += 1;
      if (openModalCount === 1) {
        document.body.style.overflow = "hidden";
      }
    } else {
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) {
        document.body.style.overflow = "";
      }
    }
  }

  get sizeClass(): string {
    const sizeMap = {
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-2xl",
      xl: "max-w-4xl",
    };
    return sizeMap[this.size];
  }

  /**
   * Close the modal and emit the change event
   */
  closeModal(): void {
    if (this.dismissible) {
      this.isOpenChange.emit(false);
    }
  }

  /**
   * Prevent event propagation when clicking inside the modal
   */
  modalClick(event: Event): void {
    event.stopPropagation();
  }
}
